// eslint-disable-next-line

import {
	ExportOutlined,
	PlusCircleFilled,
	SearchOutlined,
	UserOutlined,
} from "@ant-design/icons";
import {
	DatePicker,
	Input,
	Modal,
	message,
	notification,
	Pagination,
	Progress,
	Select,
	Table,
	Tooltip,
} from "antd";
import debounce from "lodash.debounce";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import CreateActivityAdminForm from "../components/CreateActivityAdminForm";
import CreateActivityForm from "../components/CreateActivityForm";
import EditActivityForm from "../components/EditActivityForm";
import BASE_URL, { COMPANY_CHOICES as COMPANY_OPTIONS } from "../constants";
import AuthContext from "../context/AuthContext";
import { ACTIVITY_TYPE_FILTERS } from "../utils/activityTypes";
import { renderNullable, renderNullablePairTag } from "../utils/renderNullable";
import useAxios from "../utils/useAxios";

// Issue #88: 押せないボタンの「なぜ押せないか」を hover で示すための文言。
// 編集期限の判定は isEditButtonDisabled（翌月 10 日まで編集可）と対応させる。
export const EDIT_DISABLED_REASON =
	"The editing period has closed. Activities can be edited until the 10th of the following month.";
export const EXPORT_DISABLED_REASON =
	"Only administrators can export the timesheet.";

// Issue #123: 実在の会社は constants.js の COMPANY_CHOICES が唯一の定義。ここで
// 配列を書き起こすと、会社が追加されたときにこの画面だけ取り残される（TD がまさに
// その状態だった）。先頭のセンチネルだけをこの画面の都合として足す。
export const COMPANY_CHOICES = [
	{ value: -1, label: "All Company" },
	...COMPANY_OPTIONS,
];

// "All Company" は「会社で絞り込まない（全社一括）」の意図。実在の会社コードではないため
// バックエンドへは company パラメータ自体を送らない。
const NONE_COMPANY_VALUE = COMPANY_CHOICES[0].value;

// Department にも同様の「絞り込まない」選択肢を用意する。送信時は department
// パラメータ自体を送らない。Issue #154 で選択値を Department.id（数値）へ切り替えた
// ため、この文字列センチネルが実在の選択肢と衝突することはない。
const ALL_DEPARTMENTS_VALUE = "__ALL_DEPARTMENTS__";

// ファイル名に使えない文字（\ / : * ? " < > | と空白）を "-" に置換する。
const sanitizeForFilename = (value) => value.replace(/[\\/:*?"<>|\s]/g, "-");

// Issue #69: 4社一括Excel出力の進捗表示を出力処理全体ベースの割合にする。
// サーバー側(export_all_status)はシート構築フェーズを0〜EXPORT_ALL_SHEET_BUILD_PERCENT_CAP
// にマップして返す(保存/仕上げ中はこの値で足踏み)。残りはダウンロード転送分として
// フロント側でEXPORT_DOWNLOAD_PERCENT_SPANだけ加算し、100%はダウンロード完了時のみ到達する。
// Issue #83: 一覧の表示件数。既定は backend の PAGE_SIZE(10) に合わせる。
// 選択肢の最大値は backend の ClientPageSizePagination.max_page_size と揃える。
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Issue #83: 稼働日数の並び替え。未設定(null)を含む列を素朴に引き算すると NaN で
// 順序が壊れ、Issue #58 の compareNullableText は文字列比較のため数値には使えない
// （"10" < "9" になる）。未設定は最小として扱い先頭側へ寄せる。
// テストから直接検証する目的で export する。
export const compareNullableNumber = (a, b) => {
	const left = a ?? Number.NEGATIVE_INFINITY;
	const right = b ?? Number.NEGATIVE_INFINITY;
	if (left === right) {
		return 0;
	}
	return left < right ? -1 : 1;
};

const EXPORT_ALL_SHEET_BUILD_PERCENT_CAP = 90;
const EXPORT_DOWNLOAD_PERCENT_SPAN = 100 - EXPORT_ALL_SHEET_BUILD_PERCENT_CAP;

// axiosのonDownloadProgressイベントから0〜EXPORT_DOWNLOAD_PERCENT_SPANの加算量を算出する。
// Content-Length未取得(total=0/undefined)の環境では計測不能なため0を返す
// （不確定表示のフォールバック。90%表示を維持し続け、完了時に明示的に100%へ進める）。
export const computeDownloadPercent = (progressEvent) => {
	if (!progressEvent || !progressEvent.total) {
		return 0;
	}
	const ratio = Math.min(progressEvent.loaded / progressEvent.total, 1);
	// PR #55 Codexレビュー対応(P2): Math.round だと ratio が 0.95 以上で SPAN(10) に
	// 丸め上がり、転送完了前に 100% 表示になる。floor + SPAN-1 上限で途中経過は
	// 必ず 99% 以下に抑え、100% はダウンロード完了時の明示更新のみで到達させる
	return Math.min(
		Math.floor(ratio * EXPORT_DOWNLOAD_PERCENT_SPAN),
		EXPORT_DOWNLOAD_PERCENT_SPAN - 1,
	);
};

// エクスポート進捗モーダルに表示する円グラフの割合を算出する。
// シート構築〜保存/仕上げ中はサーバー側percent(0〜EXPORT_ALL_SHEET_BUILD_PERCENT_CAP)を
// そのまま使い、ダウンロード転送中はその上限にdownloadPercentを加算する
// (100%はダウンロード完了時のみ到達する。呼び出し側でdownloadPercentを
// EXPORT_DOWNLOAD_PERCENT_SPANまで進める)。
export const computeExportModalPercent = ({
	downloadingFile,
	serverPercent,
	downloadPercent,
}) => {
	if (downloadingFile) {
		return EXPORT_ALL_SHEET_BUILD_PERCENT_CAP + downloadPercent;
	}
	return serverPercent ?? 0;
};

const padTwoDigits = (value) => String(value).padStart(2, "0");

const formatExportYYYYMM = (exportDate, now) => {
	if (exportDate) {
		return exportDate.replace("-", "");
	}
	return `${now.getFullYear()}${padTwoDigits(now.getMonth() + 1)}`;
};

const formatExportTimestamp = (now) =>
	`${now.getFullYear()}${padTwoDigits(now.getMonth() + 1)}${padTwoDigits(
		now.getDate(),
	)}${padTwoDigits(now.getHours())}${padTwoDigits(
		now.getMinutes(),
	)}${padTwoDigits(now.getSeconds())}`;

// Issue #154: Department の選択値は Department.id。出力ファイル名には id ではなく
// 部門名を入れるため、選択肢から引き直す。「All」のセンチネルと未選択はそのまま返し、
// buildExportFilename 側の "ALL" 判定に委ねる。選択肢に無い id（別の管理者が削除した
// 直後など）は id をそのまま文字列化して残す（ファイル名の生成自体は止めない）。
export const resolveDepartmentName = (value, departmentChoices) => {
	if (
		value === null ||
		value === undefined ||
		value === ALL_DEPARTMENTS_VALUE
	) {
		return value;
	}
	const choice = departmentChoices.find(
		(department) => department.id === value,
	);
	return choice ? choice.name : String(value);
};

// Issue #70: 4社一括Excel出力のファイル名を
// timesheet_出力年月_会社略称_部門略称_出力日時 の形式で組み立てる。
// finishExportAllが選択状態をクリアする前(ダウンロード実行時点)の値を渡すこと。
export const buildExportFilename = ({
	exportDate,
	companyValue,
	departmentValue,
	now = new Date(),
}) => {
	const companyChoice = COMPANY_CHOICES.find(
		(choice) => choice.value === companyValue,
	);
	const companyAbbr =
		!companyChoice || companyChoice.value === NONE_COMPANY_VALUE
			? "ALL"
			: companyChoice.label;

	const departmentAbbr =
		!departmentValue || departmentValue === ALL_DEPARTMENTS_VALUE
			? "ALL"
			: sanitizeForFilename(departmentValue);

	return `timesheet_${formatExportYYYYMM(exportDate, now)}_${companyAbbr}_${departmentAbbr}_${formatExportTimestamp(now)}.xlsx`;
};

const HomePage = () => {
	const [activities, setActivities] = useState([]);
	const { authTokens, logoutUser, user } = useContext(AuthContext);
	// Issue #88 Codex指摘: 4社一括出力の権限は backend が
	// `user.is_superuser or user.isAdmin` で判定している（views.py の
	// export_all 共通チェック）。Header の Admin 導線も同じ式（Issue #84）だが、
	// ここだけ isAdmin 単独だったため、is_superuser のみの管理者は画面上
	// 出力できず、追加した Tooltip でも「管理者ではない」と誤案内していた。
	const canExportAll = Boolean(user?.isAdmin || user?.is_superuser);
	// Issue #79: 初期値 false だと初回レンダー〜useEffect 発火の間に「No data」が
	// 一瞬見える。初期表示の useEffect は無条件で走り getUserActivities の finally が
	// 必ず解除するため、true 始まりでもスピナーが残り続けることはない。
	const [tableLoading, setTableLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [openAdminLog, setOpenAdminLog] = useState(false);
	const [myActivities, setMyActivities] = useState([]);
	const [selectedDate, setSelectedDate] = useState(null);
	const [exportModalVisible, setExportModalVisible] = useState(false);
	const [selectedExportCompany, setSelectedExportCompany] = useState(null);
	const [selectedDepartment, setSelectedDepartment] = useState(null);
	const [exportDate, setExportDate] = useState(null);
	const [exportModalMyActivities, setExportModalMyActivities] = useState(false);
	const [exportingMyActivitiesLoading, setExportingMyActivitiesLoading] =
		useState(false);
	const [selectedMyActivitiesDate, setSelectedMyActivtiesDate] = useState(null);
	// Issue #79: 同上（解除は getMyActivities の finally）
	const [myActivitiesLoading, setMyActivitiesLoading] = useState(true);
	const [adminUser, setAdminUser] = useState(null); // Define adminUser state
	const [myActivitiesCurrentPage, setMyActivitiesCurrentPage] = useState(1);
	const [userActivitiesCurrentPage, setUserActivitiesCurrentPage] = useState(1);
	// Issue #83: 表示件数を切り替えられるようにする。サーバー側ページネーションのため
	// 件数は API へ page_size として送る（backend の ClientPageSizePagination が受ける）。
	const [myActivitiesPageSize, setMyActivitiesPageSize] =
		useState(DEFAULT_PAGE_SIZE);
	const [userActivitiesPageSize, setUserActivitiesPageSize] =
		useState(DEFAULT_PAGE_SIZE);
	const [totalActivities, setTotalActivities] = useState(0);
	const [totalCActivities, setTotalCActivities] = useState(0);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [selectedActivity, setSelectedActivity] = useState(null);
	const [departmentChoices, setDepartmentChoices] = useState([]);
	const [nameInput, setNameInput] = useState("");
	const { Option } = Select;

	useEffect(() => {
		// ページ変更・日付変更のフェッチでも、現在入力中の絞り込み文字列(nameInput)を
		// 引き継ぐ。引数なしで呼ぶと params.name が undefined になり、氏名絞り込み後に
		// ページ遷移すると絞り込みが解除されてしまう(Issue #68)。
		getMyActivities(nameInput || undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedMyActivitiesDate, myActivitiesCurrentPage, myActivitiesPageSize]);

	useEffect(() => {
		getUserActivities();
	}, [selectedDate, userActivitiesCurrentPage, userActivitiesPageSize]);

	useEffect(() => {
		getDepartments();
	}, []);

	// Issue #83: 表示件数を変えるとページ数自体が変わるため先頭ページへ戻す
	// （そのままだと存在しないページを要求して空表示になる）。
	//
	// rc-pagination は件数変更時に onShowSizeChange → setCurrent → onChange の順で
	// 呼ぶため、onShowSizeChange 側でページを 1 に戻しても直後の onChange が元の
	// ページ番号で上書きしてしまう。件数の変化は onChange 側で見て一度に決める。
	const handlePageChange = (page, size) => {
		if (size !== myActivitiesPageSize) {
			setMyActivitiesPageSize(size);
			setMyActivitiesCurrentPage(1);
			return;
		}
		setMyActivitiesCurrentPage(page);
	};

	const handleCalculateActivityPageChange = (page, size) => {
		if (size !== userActivitiesPageSize) {
			setUserActivitiesPageSize(size);
			setUserActivitiesCurrentPage(1);
			return;
		}
		setUserActivitiesCurrentPage(page);
	};

	const handleAdminUserChange = (value) => {
		setAdminUser(value);
	};

	const handleDateChange = (date, dateString) => {
		setSelectedDate(dateString);
		setUserActivitiesCurrentPage(1);
	};

	const handleExportDateChange = (date, dateString) => {
		setExportDate(dateString);
	};

	const downloadExportedTimesheet = (data, filename) => {
		const blob = new Blob([data], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.style.display = "none";
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const finishExportAll = () => {
		if (exportPollTimerRef.current) {
			clearInterval(exportPollTimerRef.current);
			exportPollTimerRef.current = null;
		}
		setExporting(false);
		setExportProgress(null);
		setDownloadingFile(false);
		setDownloadPercent(0);
		setSelectedDepartment(null);
		setSelectedExportCompany(null);
		closeExportModal();
	};

	const handleExportConfirm = async () => {
		if (exporting || !canExportAll) {
			console.error("Export conditions not met");
			return;
		}

		const params = {
			company:
				selectedExportCompany === NONE_COMPANY_VALUE
					? undefined
					: selectedExportCompany,
			department:
				selectedDepartment === ALL_DEPARTMENTS_VALUE
					? undefined
					: selectedDepartment,
		};
		if (exportDate) {
			params.date = `${exportDate}-01`;
		}

		try {
			setExporting(true);
			setExportProgress(null);
			setDownloadPercent(0);

			const startResponse = await api.post(
				`${BASE_URL}/activity/export_all_async/`,
				null,
				{
					params,
					headers: {
						Authorization: "Bearer " + String(authTokens.access),
					},
				},
			);
			const jobId = startResponse.data.job_id;

			let consecutivePollFailures = 0;
			// status APIの応答がポーリング間隔(2秒)を超えると次のtickが並行して走り、
			// 完了時にdownload APIを二重に呼んでしまう。実行中フラグで常に1本に抑える。
			let pollInFlight = false;

			exportPollTimerRef.current = setInterval(async () => {
				if (pollInFlight) {
					return;
				}
				pollInFlight = true;
				try {
					const statusResponse = await api.get(
						`${BASE_URL}/activity/export_all_status/`,
						{ params: { job_id: jobId } },
					);
					consecutivePollFailures = 0;
					const {
						status: jobStatus,
						processed,
						total,
						percent,
						error_message: jobErrorMessage,
					} = statusResponse.data;
					setExportProgress({ processed, total, percent });

					if (jobStatus === "succeeded") {
						// ダウンロード自体に2秒(ポーリング間隔)以上かかると、完了後の
						// 後始末(finishExportAll)が走る前に次のtickが発火し、
						// 同じジョブを二重ダウンロードしてしまう。ダウンロード開始前に
						// interval自体は止めておく(exporting状態のリセットはダウンロード
						// 完了後のfinishExportAllにまとめる)。
						if (exportPollTimerRef.current) {
							clearInterval(exportPollTimerRef.current);
							exportPollTimerRef.current = null;
						}
						// ダウンロード自体の失敗(ネットワーク断・サーバーエラー等)は、ポーリング通信
						// 自体の失敗(consecutivePollFailures)とは別物として扱う。ここで捕捉せずに
						// 外側のcatchに落とすと、既にinterval停止済みのため3回連続に到達できず
						// 画面が"Exporting"のまま固まってしまう。必ずfinallyでfinishExportAllを呼ぶ。
						// Issue #62: ジョブ自体は完了(100%)しているが、ファイル本体の取得はこれから
						// なので、利用者には別のインジケーターで「まだ待つ理由」を示す。
						setDownloadingFile(true);
						try {
							const downloadResponse = await api.get(
								`${BASE_URL}/activity/export_all_download/`,
								{
									params: { job_id: jobId },
									responseType: "arraybuffer",
									onDownloadProgress: (progressEvent) => {
										setDownloadPercent(computeDownloadPercent(progressEvent));
									},
								},
							);
							// ダウンロード転送が実際に完了した時点でのみ100%へ進める
							// (onDownloadProgressの途中経過は99%までに抑えている)。
							setDownloadPercent(EXPORT_DOWNLOAD_PERCENT_SPAN);
							// finishExportAllが選択状態をクリアする前に、ここでファイル名を組み立てる。
							const filename = buildExportFilename({
								exportDate,
								companyValue: selectedExportCompany,
								// 選択値は Department.id だが、ファイル名には利用者が対象を
								// 判別できるよう部門名を入れる
								departmentValue: resolveDepartmentName(
									selectedDepartment,
									departmentChoices,
								),
							});
							downloadExportedTimesheet(downloadResponse.data, filename);
							message.success("User activities exported successfully");
						} catch (downloadError) {
							message.error("Failed to download exported timesheet");
						} finally {
							finishExportAll();
						}
					} else if (jobStatus === "failed") {
						message.error(
							jobErrorMessage || "Failed to export user activities",
						);
						finishExportAll();
					}
				} catch (error) {
					consecutivePollFailures += 1;
					// ポーリング自体の通信失敗（ジョブ自体のfailedとは別）が3回連続したら
					// 無限リトライせず打ち切る。ジョブ自体はサーバー側で走り続ける
					if (consecutivePollFailures >= 3) {
						message.error("Failed to export user activities");
						finishExportAll();
					}
				} finally {
					pollInFlight = false;
				}
			}, 2000);
		} catch (error) {
			message.error(
				error.response?.data?.detail || "Failed to export user activities",
			);
			finishExportAll();
		}
	};

	const isEditButtonDisabled = (activityDate) => {
		const currentDate = new Date();
		const currentMonth = currentDate.getMonth();
		const currentYear = currentDate.getFullYear();

		const activityDateObj = new Date(activityDate);
		const activityMonth = activityDateObj.getMonth();
		const activityYear = activityDateObj.getFullYear();

		// Check if we are not in the same month and the first 5 days of the following month have passed
		return (
			!(currentMonth === activityMonth && currentYear === activityYear) &&
			currentDate.getDate() > 10
		);
	};

	const getDepartments = async () => {
		try {
			// Issue #155 (D-6) 応急対応: for_selection=1 でプルダウン選択用途であることを
			// バックエンドへ明示し、空欄名・表記ゆれ重複を除いた名前順の一覧を受け取る。
			// バックエンド側の判断理由・恒久対応（Step E）は
			// backend/dashboard/views.py の DepartmentViewSet._filtered_for_selection 参照。
			const response = await api.get(`/department/`, {
				params: { for_selection: 1 },
			});

			setDepartmentChoices(response.data);
		} catch (error) {
			// Handle error
			console.error("Error fetching departments:", error);
			throw error; // Rethrow the error for additional handling if needed
		}
	};

	// Issue #88: Export 系モーダルも開いた直後に最初の入力欄（月）へフォーカスする。
	// antd(rc-dialog) は表示時にダイアログ自体へフォーカスを移すため autoFocus 属性
	// だけでは奪われる。表示の変化を見て、次のタスクで明示的に focus する。
	const exportDateRef = useRef(null);
	const myExportDateRef = useRef(null);

	useEffect(() => {
		if (!exportModalVisible) return undefined;
		const timer = setTimeout(() => exportDateRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [exportModalVisible]);

	useEffect(() => {
		if (!exportModalMyActivities) return undefined;
		const timer = setTimeout(() => myExportDateRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [exportModalMyActivities]);

	const renderExportModalContent = () => {
		if (exporting) {
			// Issue #69: 出力処理全体(シート構築→保存/仕上げ→ダウンロード転送)を
			// 通して単調増加する割合にする。
			const percent = computeExportModalPercent({
				downloadingFile,
				serverPercent: exportProgress?.percent,
				downloadPercent,
			});
			return (
				<div style={{ textAlign: "center", padding: "40px 0" }}>
					<Progress type="circle" percent={percent} />
					<p style={{ marginTop: 16 }}>
						{downloadingFile
							? "Preparing file..."
							: exportProgress
								? `${exportProgress.processed} / ${exportProgress.total} processed`
								: "Starting export..."}
					</p>
				</div>
			);
		}
		return (
			<div>
				<div style={{ margin: 10 }}>
					<label style={{ display: "block" }}>Date:</label>
					<DatePicker
						ref={exportDateRef}
						style={{ marginBottom: "10px" }}
						onChange={handleExportDateChange}
						picker="month"
					/>
					<label style={{ display: "block" }}>Company:</label>
					<Select
						placeholder="Select Company"
						style={{ width: "100%" }}
						value={selectedExportCompany ?? undefined}
						onChange={(value) => setSelectedExportCompany(value)}
					>
						{COMPANY_CHOICES.map(({ value, label }) => (
							<Option key={value} value={value}>
								{label}
							</Option>
						))}
					</Select>
				</div>
				<div style={{ margin: 10 }}>
					<label>Department:</label>
					<Select
						placeholder="Select Department"
						style={{ width: "100%" }}
						value={selectedDepartment ?? undefined}
						onChange={(value) => setSelectedDepartment(value)}
					>
						<Option key={ALL_DEPARTMENTS_VALUE} value={ALL_DEPARTMENTS_VALUE}>
							All
						</Option>
						{/* Issue #154: 送信値は Department.id。表記ゆれで同名に見える別部門が
						    存在しうる移行期に、名前で送ると別の部門の利用者が抽出される。
						    バックエンド（Issue #153）は id・名前のどちらでも照合できる */}
						{departmentChoices.map((department) => (
							<Option key={department.id} value={department.id}>
								{department.name}
							</Option>
						))}
					</Select>
				</div>
			</div>
		);
	};

	const renderMyTimesheetModalContent = () => {
		if (exportingMyActivitiesLoading) {
			// Issue #87: Export All モーダルと同様、処理中は進捗であることが
			// 分かるテキストを表示し、フォーム入力欄は隠す。
			return (
				<div style={{ textAlign: "center", padding: "40px 0" }}>
					<p>Exporting your timesheet...</p>
				</div>
			);
		}
		return (
			<div>
				<div style={{ margin: 10 }}>
					<label style={{ display: "block" }}>Date:</label>
					<DatePicker
						ref={myExportDateRef}
						onChange={handleMyActivitiesDateChange}
						picker="month"
					/>
				</div>
			</div>
		);
	};

	const [exporting, setExporting] = useState(false);
	const [exportProgress, setExportProgress] = useState(null);
	const [downloadingFile, setDownloadingFile] = useState(false);
	// Issue #69: ダウンロード転送分(サーバー側percentの90%到達後〜完了)の進捗を
	// 0〜EXPORT_DOWNLOAD_PERCENT_SPANで保持する。Content-Lengthが取得できない
	// 環境では0のまま(=不確定表示のフォールバック。90%表示を維持し続ける)。
	const [downloadPercent, setDownloadPercent] = useState(0);
	const exportPollTimerRef = useRef(null);

	useEffect(() => {
		return () => {
			if (exportPollTimerRef.current) {
				clearInterval(exportPollTimerRef.current);
			}
		};
	}, []);

	const openExportModal = () => {
		setExportModalVisible(true);
	};

	const closeExportModal = () => {
		setSelectedDepartment(null);
		setSelectedExportCompany(null); // Set it to null to clear the selected company
		setExportModalVisible(false);
	};

	const exportExcelActivities = async () => {
		// Open the export modal
		openExportModal();
	};

	const openExportMyActivitiesModal = () => {
		setExportModalMyActivities(true);
	};

	const exportExcelMyActivities = async () => {
		// Open the export modal
		openExportMyActivitiesModal();
	};

	const closeMyActivitiesExportModal = () => {
		setExportModalMyActivities(false);
	};

	const handleMyActivitiesDateChange = (date, dateString) => {
		setSelectedMyActivtiesDate(dateString);
		setMyActivitiesCurrentPage(1);
	};

	const handleExportMyActivities = async () => {
		try {
			setExportingMyActivitiesLoading(true); // Set the loading state to true
			let url = `/files/activities/own_timesheet/${user.user_id}`;

			if (selectedMyActivitiesDate) {
				const formattedDate = `${selectedMyActivitiesDate}-01`;
				url += `?date=${formattedDate}`;
			}
			const response = await api.get(url, {
				responseType: "arraybuffer", // Set the response type to 'arraybuffer'
			});

			if (response.status === 200) {
				const blob = new Blob([response.data], {
					type: "application/ms-excel",
				});
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.style.display = "none";
				a.href = url;
				a.download = `my_timesheet_${selectedMyActivitiesDate}_${user.username}.xlsx`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				message.success("User activities exported successfully");
			} else {
				message.error("Failed to export user activities");
			}
		} catch (error) {
			message.error("Failed to export user activities");
		} finally {
			setExportingMyActivitiesLoading(false); // Set the loading state back to false when the export is finished
			closeMyActivitiesExportModal();
		}
	};

	const api = useAxios();

	// getMyActivities は下で再定義されるたびに新しいクロージャになるが、
	// ref 経由で常に最新版を呼び出すことで debounce インスタンス自体は
	// マウント中1つに固定する（毎レンダーで生成し直すとdebounceが無効化される）。
	const getMyActivitiesRef = useRef();

	const debouncedGetMyActivities = useMemo(
		() =>
			debounce((name) => {
				if (!name) return; // If the input is empty, do not make the API call
				getMyActivitiesRef.current(name);
			}, 500),
		[],
	);

	useEffect(() => {
		return () => debouncedGetMyActivities.cancel();
	}, [debouncedGetMyActivities]);

	// Handle name input changes
	const handleNameInputChange = (e) => {
		setMyActivitiesCurrentPage(1);
		const value = e.target.value;
		setNameInput(value); // Update input value

		if (value) {
			debouncedGetMyActivities(value); // Call the debounce function with the new value
		} else {
			// When input is empty, cancel any pending debounced filtered request
			// before fetching all activities (Issue #66: without cancel, a
			// filtered request scheduled just before clearing could still fire
			// afterward and unexpectedly re-filter the list).
			debouncedGetMyActivities.cancel();
			// When input is empty, call getMyActivities to fetch all activities
			getMyActivities(); // Fetch all activities
		}
	};

	const columns = [
		{
			title: "First Name",
			dataIndex: "user__first_name",
			key: "user__first_name",
			render: (text, record) => (
				<p>{renderNullable(record.user__first_name)}</p>
			),
		},
		{
			title: "Last Name",
			dataIndex: "user__last_name",
			key: "user__last_name",
			render: (text, record) => <p>{renderNullable(record.user__last_name)}</p>,
		},
		{
			title: "Email",
			dataIndex: "user__email",
			key: "user__email",
			render: (text, record) => <p>{renderNullable(record.user__email)}</p>,
		},
		{
			title: "Working Days (C)",
			dataIndex: "working_days",
			// Issue #83: antd は列キーで並び替え状態を対応付けるため、C と J が同じ
			// キーだと両方の見出しが選択中になり、どちらで並べているか分からなくなる。
			key: "working_days_cairo",
			// Issue #83: 稼働日数の多い / 少ない人を探せるようにする。
			// サーバー側ページネーションのため、並び替えは表示中のページ内で効く。
			sorter: (a, b) =>
				compareNullableNumber(a.working_days_cairo, b.working_days_cairo),
			render: (text, record) =>
				renderNullablePairTag(
					record.working_days_cairo,
					record.total_days_cairo,
					"blue",
					record.id,
				),
		},
		{
			title: "Working Days (J)",
			dataIndex: "working_days",
			key: "working_days_japan",
			sorter: (a, b) =>
				compareNullableNumber(a.working_days_japan, b.working_days_japan),
			render: (text, record) =>
				renderNullablePairTag(
					record.working_days_japan,
					record.total_days_japan,
					"red",
					record.id,
				),
		},
	];

	const my_activities_data_source = myActivities.map((item) => ({
		...item,
		key: item.id,
	}));

	const my_activities_column = [
		{
			title: "First Name",
			dataIndex: "firstName",
			key: "firstName",
			render: (text, record) => <p>{renderNullable(record.firstName)}</p>,
		},
		{
			title: "Last Name",
			dataIndex: "lastName",
			key: "lastName",
			render: (text, record) => <p>{renderNullable(record.lastName)}</p>,
		},
		{
			title: "Activity Type",
			dataIndex: "activityType",
			key: "activityType",
			render: (text, record) => <p>{renderNullable(record.activityType)}</p>,
			// Issue #83: 勤怠種別で絞り込めるようにする（表示中のページ内）。
			filters: ACTIVITY_TYPE_FILTERS,
			onFilter: (value, record) => record.activityType === value,
		},
		{
			title: "Activity Date",
			dataIndex: "activityDate",
			key: "activityDate",
			render: (text, record) => <p>{renderNullable(record.activityDate)}</p>,
		},
		{
			title: "Activity",
			dataIndex: "userActivity",
			key: "userActivity",
			width: "60%",
			render: (text, record) => <p>{renderNullable(record.userActivity)}</p>,
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			width: "13%",
			render: (text, record) => {
				const editDisabled = isEditButtonDisabled(record.activityDate);
				const editButton = (
					<Button
						type="primary"
						disabled={editDisabled}
						onClick={() => {
							setEditModalVisible(true);
							setSelectedActivity(record); // record is the current activity being edited
						}}
					>
						Edit/Delete Activity
					</Button>
				);

				// Issue #88: 押せない理由（編集期限切れ）が画面から分からなかった。
				// disabled なボタンはマウスイベントを発火しない（Bootstrap が
				// pointer-events: none を当てる）ため、span で包んでそちらへ Tooltip を付ける。
				return editDisabled ? (
					<Tooltip title={EDIT_DISABLED_REASON} mouseEnterDelay={0}>
						<span style={{ display: "inline-block" }}>{editButton}</span>
					</Tooltip>
				) : (
					editButton
				);
			},
		},
	];

	const columns_data_source = activities.map((item) => ({
		...item,
		key: item.id,
	}));

	const handleAdminCreateActivity = async (values) => {
		try {
			await api.post(`/activity/admin/log/${values.adminUser}/`, {
				userActivity: values.adminActivity,
				activityType: values.adminActivityType,
				activityDate: values.adminActivityDate,
			});
			getMyActivities();
			getUserActivities();

			notification.success({
				message: "Create Activity Success",
				// Issue #88: 代理登録は「誰の記録を作ったか」が分からないと確認できない。
				// 対象者名はフォーム側が選択中ユーザーから解決して渡す。
				description: values.adminUsername
					? `The activity for ${values.adminUsername} has been created.`
					: "The activity has been created.",
			});

			setOpenAdminLog(false);
		} catch (error) {
			const description = `Failed to create activity. ${
				error.response?.data?.detail ?? "Unexpected error occurred"
			}`;
			notification.error({
				message:
					error.response?.status === 500
						? "Server Error"
						: "Create Activity Error",
				description,
			});

			setOpenAdminLog(false);

			// Only trigger getUserActivities if there's an actual error
			if (error.response && error.response.status !== 500) {
				getUserActivities();
			}
		}
	};

	// Create activity
	const handleCreateActivity = async (values) => {
		try {
			// Make a POST request to create a new activity

			await api.post(`/activity/create_activity/`, {
				userActivity: values.Activity,
				activityType: values.activityType,
				activityDate: values.activityDate,
			});
			getMyActivities();
			getUserActivities();

			notification.success({
				message: "Create Activity Success",
				description: "Your activity has been created.",
			});

			// Close the modal
			setOpen(false);
		} catch (error) {
			const description = `Failed to create activity. ${
				error.response?.data?.detail ?? "Unexpected error occurred"
			}`;
			notification.error({
				message:
					error.response?.status === 500
						? "Server Error"
						: "Create Activity Error",
				description,
			});

			setOpen(false);

			// Only trigger getUserActivities if there's an actual error
			if (error.response && error.response.status !== 500) {
				getUserActivities();
			}
		}
	};

	const getUserActivities = async () => {
		setTableLoading(true);

		try {
			const params = {
				page: userActivitiesCurrentPage,
				page_size: userActivitiesPageSize,
			};
			let url = "activity/calculate_activity/";

			if (selectedDate) {
				const formattedDate = `${selectedDate}-01`;
				url += `?date=${formattedDate}`;
			}

			const response = await api.get(url, { params });
			setActivities(response.data.results);
			setTotalCActivities(response.data.count);
		} catch (error) {
			if (error.response?.status === 401) {
				logoutUser({ sessionExpired: true });
			} else if (error.response?.status === 403) {
				// calculate_activity は管理者専用エンドポイント。一般ユーザーでも初期表示の
				// useEffect から必ず呼ばれるため、権限不足による 403 は想定内の応答であり
				// ユーザーに見せるべきエラーではない（Issue #54 のエラー可視化対象外）。
				// 認証切れ(401)とは異なりログアウトもしない。
				console.error(
					"Failed to fetch user activities (insufficient permission):",
					error,
				);
			} else {
				console.error("Failed to fetch user activities:", error);
				message.error(
					error.response?.data?.detail ?? "Failed to fetch user activities",
				);
			}
		} finally {
			setTableLoading(false);
		}
	};

	const getMyActivities = async (name) => {
		setMyActivitiesLoading(true);
		try {
			let url = "activity/my_activities";
			const params = {
				page: myActivitiesCurrentPage,
				page_size: myActivitiesPageSize,
				name,
			};
			if (selectedMyActivitiesDate) {
				const formattedDate = `${selectedMyActivitiesDate}-01`;
				url += `?date=${formattedDate}`;
			}

			const response = await api.get(url, { params });
			setMyActivities(response.data.results);
			setTotalActivities(response.data.count);
		} catch (error) {
			if (error.response?.status === 401) {
				logoutUser({ sessionExpired: true });
			} else {
				console.error("Failed to fetch my activities:", error);
				message.error(
					error.response?.data?.detail ?? "Failed to fetch my activities",
				);
			}
		} finally {
			setMyActivitiesLoading(false);
		}
	};
	// レンダーごとに最新の getMyActivities を ref へ反映（debounce 対象の実体を更新）
	getMyActivitiesRef.current = getMyActivities;

	const handleUpdateActivity = async (activityId, values) => {
		try {
			// Make a PATCH request to update the activity
			setMyActivitiesLoading(true);
			await api.patch(`/activity/edit_activity/${activityId}/`, values);

			// Refresh the activities list
			getMyActivities();
			setEditModalVisible(false);
			setMyActivitiesLoading(false);
			notification.success({
				message: "Edit Activity Success",
				description: `Activity updated successfully`,
			});
		} catch (error) {
			getMyActivities();
			setEditModalVisible(false);
			setMyActivitiesLoading(false);
			notification.error({
				message: "Edit Activity Failure",
				description: "Failed to update activity!",
			});
		}
	};

	const handleDeleteActivity = async (activityId) => {
		try {
			// Make a delete request to update the activity
			setMyActivitiesLoading(true);
			await api.delete(`/activity/delete_activity/${activityId}/`);

			// Refresh the activities list
			getMyActivities();
			setEditModalVisible(false);
			setMyActivitiesLoading(false);
			notification.success({
				message: "Delete Activity Success",
				description: `Activity deleted successfully`,
			});
		} catch (error) {
			getMyActivities();
			setEditModalVisible(false);
			setMyActivitiesLoading(false);
			notification.error({
				message: "Delete Activity Failure",
				description: "Failed to delete activity!",
			});
		}
	};

	// Issue #88: Export Timesheet は管理者以外では押せないが、その理由が画面に出て
	// いなかった。disabled なボタンはマウスイベントを発火しないため span で包み、
	// 管理者のときは title を空にして Tooltip を出さない（antd は falsy な title を描画しない）。
	const exportTimesheetButton = (
		<Tooltip
			title={canExportAll ? "" : EXPORT_DISABLED_REASON}
			mouseEnterDelay={0}
		>
			<span style={{ display: "inline-block", width: "40%" }}>
				<Button
					type="primary"
					variant="success"
					onClick={exportExcelActivities}
					style={{ width: "100%" }}
					disabled={exporting || !canExportAll} // Use the disabled prop
				>
					{exporting ? "Exporting..." : "Export Timesheet "} <ExportOutlined />
				</Button>
			</span>
		</Tooltip>
	);

	return (
		<div
			style={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<div
				style={{
					backgroundColor: "#f8f9fa",
					width: "90%",
					marginTop: 15,
					borderRadius: 6,
				}}
			>
				<h1 style={{ fontSize: 18 }}>NOCE Dashboard</h1>
			</div>
			<div style={{ width: "90%", overflowX: "auto", margin: 15 }}>
				<div style={{ margin: 10 }}>
					<DatePicker onChange={handleDateChange} picker="month" />
				</div>

				<Table
					columns={columns}
					dataSource={columns_data_source}
					rowKey={(record) => record.user__id}
					style={{ width: "100%" }} // Set the width to 100%
					columnWidth={100}
					loading={tableLoading}
					size={"middle"}
					pagination={false}
					disabled
				/>
				<Pagination
					style={{ marginTop: "10px", textAlign: "right", marginRight: "15px" }}
					current={userActivitiesCurrentPage}
					pageSize={userActivitiesPageSize}
					onChange={handleCalculateActivityPageChange}
					total={totalCActivities}
					showSizeChanger
					pageSizeOptions={PAGE_SIZE_OPTIONS}
					showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
				/>
				<div
					style={{
						backgroundColor: "#f8f9fa",
						marginTop: 40,
						height: 40,
						alignItems: "center",
						display: "flex",
						borderRadius: 6,
					}}
				>
					<h1 style={{ fontSize: 18, marginLeft: 15 }}>
						{user.isAdmin || user.is_superuser
							? `All Activities`
							: `My Activities`}
					</h1>
				</div>
				<div style={{ margin: 10 }}>
					<DatePicker onChange={handleMyActivitiesDateChange} picker="month" />
				</div>
				<div style={{ margin: 10 }}>
					<Input
						prefix={<UserOutlined />}
						allowClear
						placeholder="Search by name"
						value={nameInput}
						onChange={handleNameInputChange}
						style={{ width: "100%", maxWidth: "180px" }} // Full width, but max 400px
					/>
				</div>
				<div style={{ width: "100%", overflowX: "auto", margin: 15 }}>
					<Table
						columns={my_activities_column}
						dataSource={my_activities_data_source}
						rowKey={(record) => record.id}
						style={{ width: "100%" }} // Set the width to 100%
						columnWidth={100}
						size={"middle"}
						loading={myActivitiesLoading}
						pagination={false}
						disabled
					/>
					<Pagination
						style={{
							marginTop: "10px",
							textAlign: "right",
							marginRight: "15px",
						}}
						current={myActivitiesCurrentPage}
						pageSize={myActivitiesPageSize}
						onChange={handlePageChange}
						total={totalActivities}
						showSizeChanger
						pageSizeOptions={PAGE_SIZE_OPTIONS}
						showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
					/>
				</div>
			</div>
			<div style={{ display: "flex", width: "90%", justifyContent: "center" }}>
				{user.isAdmin || user.is_superuser ? (
					<Button
						type="primary"
						onClick={() => {
							setOpenAdminLog(true);
						}}
						style={{ width: "40%" }}
					>
						Log User Activity <PlusCircleFilled />
					</Button>
				) : (
					<Button
						type="primary"
						onClick={() => {
							setOpen(true);
						}}
						style={{ width: "40%" }}
					>
						Log Activity <PlusCircleFilled />
					</Button>
				)}
				<CreateActivityForm
					open={open}
					onCreate={handleCreateActivity}
					onCancel={() => setOpen(false)}
				/>
				<CreateActivityAdminForm
					open={openAdminLog}
					onCreate={handleAdminCreateActivity}
					onCancel={() => setOpenAdminLog(false)}
					adminUser={adminUser} // Pass adminUser state as a prop
					onAdminUserChange={handleAdminUserChange}
				/>
			</div>
			<div
				style={{
					display: "flex",
					width: "90%",
					margin: 15,
					justifyContent: "center",
				}}
			>
				{exportTimesheetButton}
				<Modal
					title="Export Timesheet"
					open={exportModalVisible}
					onOk={handleExportConfirm}
					confirmLoading={exporting}
					onCancel={closeExportModal}
					closable={!exporting}
					maskClosable={!exporting}
					keyboard={!exporting}
					cancelButtonProps={{ disabled: exporting }}
					okButtonProps={{
						disabled:
							exporting ||
							!exportDate ||
							selectedExportCompany === null ||
							selectedDepartment === null,
					}}
					key={exportModalVisible}
				>
					{renderExportModalContent()}
				</Modal>
			</div>

			{/* ページ最下部のボタン。画面の下端に貼り付かないよう下に余白を取る */}
			<div
				style={{
					display: "flex",
					width: "90%",
					justifyContent: "center",
					marginBottom: 50,
				}}
			>
				<Button
					type="primary"
					variant="success"
					onClick={exportExcelMyActivities}
					style={{ width: "40%" }}
					disabled={exportingMyActivitiesLoading}
				>
					{exportingMyActivitiesLoading
						? "Exporting My Timesheet.."
						: "Export My Timesheet "}{" "}
					<ExportOutlined />
				</Button>
				<Modal
					title="Export My Individual Timesheet"
					open={exportModalMyActivities}
					onOk={handleExportMyActivities}
					confirmLoading={exportingMyActivitiesLoading}
					onCancel={closeMyActivitiesExportModal}
					closable={!exportingMyActivitiesLoading}
					maskClosable={!exportingMyActivitiesLoading}
					keyboard={!exportingMyActivitiesLoading}
					cancelButtonProps={{ disabled: exportingMyActivitiesLoading }}
					key={exportModalMyActivities}
				>
					{renderMyTimesheetModalContent()}
				</Modal>
			</div>

			<EditActivityForm
				open={editModalVisible}
				onCancel={() => setEditModalVisible(false)}
				onDelete={handleDeleteActivity}
				onUpdate={handleUpdateActivity}
				selectedActivity={selectedActivity}
			/>
		</div>
	);
};

export default HomePage;
